import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  MiCheckbox,
  MiIcon,
  MiIconButton,
  MiInputSelectBox,
  MiInputTextbox,
  MiStackedTypography,
} from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { InvoiceStatus } from "@miplatform/types";
import { formatDate, getCurrencyString } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  mdiCheck,
  mdiPencilOutline,
  mdiTrashCanOutline,
  mdiWindowClose,
} from "@mdi/js";
import { Box, CircularProgress, GridLegacy, Tooltip } from "@mui/material";
import {
  INVOICING_ROUTES,
  RECONCILIATION_STEPS,
} from "../../accountingConstants";
import { INVOICE_ITEM_DIFF_CONSTANTS } from "../../utils/utils";
import { useReconcileContext } from "../ReconcileContext";
import { formatOpenBalanceDisplay } from "../reconcileConstants";

export const ITEM_TYPES = {
  INVOICE: "invoice",
  PAYMENT: "payment",
  VARIANCE: "variance",
};

// Unified validation schemas for different item types
const getValidationSchema = (
  itemType,
  purchaseOrders = [],
  originalPaidOnBillingCommitment = 0,
  originalBillingCommitmentId = "",
  varianceOpenBalance = 0,
  originalVarianceUnitPrice = 0
) => {
  const baseSchema = {
    name: z.string().min(1, "Required"),
    quantity: z.coerce
      .number()
      .int("Quantity must be a whole number")
      .min(0, "Quantity cannot be negative"),
    unitPrice: z.coerce.number(),
  };

  switch (itemType) {
    case ITEM_TYPES.INVOICE:
      return z.object(baseSchema);

    case ITEM_TYPES.PAYMENT:
      return z
        .object({
          billingCommitmentId: z.string().min(1, "Required"),
          paidOnBillingCommitment: z.coerce.number(),
        })
        .refine(
          (data) => {
            const isPoVoided = purchaseOrders.find(
              (po) => po.id === data.billingCommitmentId
            )?.voidDate;
            return !isPoVoided;
          },
          {
            path: ["billingCommitmentId"],
            message: "Voided Payment",
          }
        )
        .superRefine((data, ctx) => {
          const po = purchaseOrders.find(
            (p) => p.id === data.billingCommitmentId
          );
          if (!po) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["billingCommitmentId"],
              message: "Po required",
            });
            return;
          }

          const paid = data.paidOnBillingCommitment || 0;

          if (po.voidDate && paid > 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["paidOnBillingCommitment"],
              message: "No Funds",
            });
            return;
          }

          const originalPaidNum = Number(originalPaidOnBillingCommitment || 0);
          const allowedMax =
            data.billingCommitmentId === originalBillingCommitmentId
              ? (po.openBalance ?? 0) + originalPaidNum
              : po.openBalance ?? 0;

          if (
            (paid > 0 && paid > allowedMax) ||
            (paid < 0 && paid < allowedMax)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["paidOnBillingCommitment"],
              message: "Payment overdrawn",
            });
          }
        });

    case ITEM_TYPES.VARIANCE:
      return z
        .object({
          invoiceId: z.string().min(1, "Required"),
          unitPrice: z.coerce.number().min(0, "Amount cannot be negative"),
        })
        .superRefine((data, ctx) => {
          const maxAllowed =
            Math.abs(varianceOpenBalance) +
            Number(originalVarianceUnitPrice || 0);
          if (data.unitPrice > maxAllowed) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["unitPrice"],
              message: `Cannot exceed ${getCurrencyString(maxAllowed)}`,
            });
          }
        });

    default:
      return z.object(baseSchema);
  }
};

export const AccountingInvoiceItem = ({
  item = {},
  itemType = ITEM_TYPES.INVOICE, // 'invoice' | 'payment' | 'variance'
  invoiceStatus,
  readOnly = false, // prop for making the invoiceItemVarianceType = "Variance" readOnly in all cases
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(!!item?.isNew);
  const [isSaving, setIsSaving] = useState(false);
  const isSubmitting = !!item.isSubmitting;

  const {
    activeStep,
    isInvoiceMode,
    setItemVerificationStatus,
    verificationState,
    getGridConfig,
    updateInvoiceFormState,
    viewMode,
    purchaseOrders,
    purchaseOrderOptions,
    invoices,
  } = useReconcileContext();
  const { classes, theme } = useStyles();

  const gridConfig = getGridConfig(itemType);

  const isItemVerified = verificationState[item.id] === true;
  const isItemDeleted = item?.status === INVOICE_ITEM_DIFF_CONSTANTS.DELETED;

  const isFormReadOnly =
    !isEditing || isSubmitting || invoiceStatus === InvoiceStatus.Voided;

  // Get default values based on item type
  const getDefaultValues = () => {
    const base = {
      name: item?.name || "",
      quantity: item?.quantity || 1,
      unitPrice: item?.unitPrice || 0,
    };

    switch (itemType) {
      case ITEM_TYPES.PAYMENT:
        return {
          billingCommitmentId: item?.billingCommitmentId?.toString() || "",
          paidOnBillingCommitment: item?.paidOnBillingCommitment || 0,
        };
      case ITEM_TYPES.VARIANCE:
        return {
          invoiceId: item?.invoiceId?.toString() || "",
          unitPrice: item?.unitPrice || 0,
        };
      default:
        return base;
    }
  };

  // Get validation schema based on item type
  const originalPaidOnBillingCommitment = item.paidOnBillingCommitment || 0;
  const originalBillingCommitmentId =
    item.billingCommitmentId?.toString() || "";
  const originalVarianceUnitPrice = item.unitPrice || 0;
  const validationSchema = getValidationSchema(
    itemType,
    purchaseOrders,
    originalPaidOnBillingCommitment,
    originalBillingCommitmentId,
    item.openBalance,
    originalVarianceUnitPrice
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    trigger,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: "onSubmit",
    defaultValues: getDefaultValues(),
    resolver: zodResolver(validationSchema),
  });

  // Only watch these fields for invoice items
  const unitPrice = itemType === ITEM_TYPES.INVOICE ? watch("unitPrice") : "";
  const quantity = itemType === ITEM_TYPES.INVOICE ? watch("quantity") : "";

  const totalValueOfItem =
    itemType === ITEM_TYPES.INVOICE ? unitPrice * (quantity || 1) : 0;

  // this is needed to keep the form state in the context for overall validation especially in footer
  useEffect(() => {
    updateInvoiceFormState(item.id, {
      isValid,
      isEditing,
      isDirty,
      isVarianceItem: itemType === ITEM_TYPES.VARIANCE,
    });
  }, [item.invoiceId, isValid, isEditing, isDirty]);

  // Auto-uncheck checkbox if form becomes invalid
  useEffect(() => {
    // isDirty added bc of variance can be checked without editing
    if (!isValid && isItemVerified && isDirty) {
      setItemVerificationStatus(item.id, false);
    }
  }, [isValid, isItemVerified]);

  // Reset form when item prop changes for external updates
  useEffect(() => {
    if (!isEditing) {
      const newDefaultValues = getDefaultValues();
      reset(newDefaultValues);
    }
  }, [item.unitPrice, item.quantity]);

  const handleVerifyInvoiceItem = () => {
    // if form has an error do not allow verifying
    if (Object.keys(errors).length > 0) return;
    setItemVerificationStatus(item.id, !isItemVerified);
  };

  const handleStartEditing = () => {
    clearErrors();
    setIsEditing(true);
  };

  const handleSave = async (data) => {
    if (isSaving) return;
    setIsSaving(true);

    const updatedItem = {
      ...item,
      ...data,
    };

    let updateSuccessful = false;

    try {
      await onUpdate(updatedItem);
      updateSuccessful = true;
    } catch (error) {
      updateSuccessful = false;
    } finally {
      if (updateSuccessful) {
        updateInvoiceFormState(item.id, null);
        setIsEditing(false);
      } else {
        reset(getDefaultValues());
      }

      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    onDelete(item.id);
  };

  const handleCancel = () => {
    if (item.tempId && item.isNew) {
      onDelete(item.id);
      updateInvoiceFormState(item.id, null);
      return;
    }

    reset(getDefaultValues());
    setIsEditing(false);
    setIsSaving(false);
    trigger();
  };

  const getColorOfInvoiceItem = () => {
    return theme.palette.text.primary;
  };

  const getIconButtonProps = (onClick, testId) => ({
    onClick,
    "data-testid": testId,
    size: "small",
  });

  const renderSubmittingState = () => (
    <Box display="flex" alignItems="center" justifyContent="center" width={60}>
      <CircularProgress size={20} color="primary" />
    </Box>
  );

  const renderEditingControls = () => (
    <>
      <MiIconButton
        {...getIconButtonProps(
          handleSubmit(handleSave),
          "save-invoice-item-btn"
        )}
        disabled={isSaving}
      >
        <MiIcon color={theme.palette.primary.main} path={mdiCheck} size={0.8} />
      </MiIconButton>
      <MiIconButton
        {...getIconButtonProps(handleCancel, "close-invoice-item-btn")}
        disabled={isSaving}
      >
        <MiIcon
          color={theme.palette.red.main}
          path={mdiWindowClose}
          size={0.8}
        />
      </MiIconButton>
    </>
  );

  const renderDefaultControls = () => (
    <>
      <MiIconButton
        {...getIconButtonProps(handleStartEditing, "edit-invoice-item-btn")}
        sx={{
          visibility:
            readOnly || itemType === ITEM_TYPES.PAYMENT ? "hidden" : "visible",
        }}
      >
        <MiIcon
          color={theme.palette.grey.main}
          path={mdiPencilOutline}
          size={0.8}
        />
      </MiIconButton>
      <MiIconButton
        {...getIconButtonProps(handleDelete, "delete-invoice-item-btn")}
        sx={{ visibility: readOnly ? "hidden" : "visible" }}
        disabled={itemType === ITEM_TYPES.VARIANCE && !getValues("invoiceId")}
      >
        <MiIcon
          color={theme.palette.grey.main}
          path={mdiTrashCanOutline}
          size={0.8}
        />
      </MiIconButton>
    </>
  );

  let backgroundColor = "transparent";
  if (isSubmitting) {
    backgroundColor = "rgba(3, 169, 244, 0.05)";
  }
  if (isItemVerified) {
    backgroundColor = "transparent";
  }

  const itemBackgroundColor = readOnly
    ? "grey.200"
    : theme.palette.common.white;

  const invoiceIdOptions = useMemo(
    () =>
      invoices
        .filter((invoice) => invoice.status === InvoiceStatus.Draft)
        .map((invoice) => ({
          label: invoice.name,
          value: invoice.id,
        })),
    [invoices]
  );

  const getDataTestId = (itemType) => {
    if (itemType === ITEM_TYPES.INVOICE) {
      return "invoicing-invoice-item";
    }
    if (itemType === ITEM_TYPES.VARIANCE) {
      return "invoicing-variance-item";
    }
    return "invoicing-payment-item";
  };

  return (
    <Box
      data-testid={getDataTestId(itemType)}
      sx={{
        backgroundColor,
        opacity: isSubmitting ? 0.8 : 1,
        transition: "background-color 0.2s ease",
      }}
    >
      {/* It should stay flex-start to align the form errors breaking the layout */}
      <GridLegacy container alignItems="flex-start" columnSpacing={0} py={2}>
        {/* checkbox */}

        {isInvoiceMode &&
          !readOnly &&
          (invoiceStatus === InvoiceStatus.Draft ||
            itemType === ITEM_TYPES.VARIANCE) && (
            <GridLegacy item xs={0.75} sx={{ alignSelf: "stretch" }}>
              {!isEditing && !isItemDeleted && (
                <Box className={classes.checkboxCell}>
                  <MiCheckbox
                    value={isItemVerified || false}
                    handleChange={handleVerifyInvoiceItem}
                    size="medium"
                    style={{ padding: 0 }}
                  />
                </Box>
              )}
            </GridLegacy>
          )}

        <GridLegacy item xs>
          <GridLegacy container columnSpacing={1} rowSpacing={2}>
            {/* Basic Invoice Fields - only for invoice items */}
            {itemType === ITEM_TYPES.INVOICE && (
              <>
                <GridLegacy item xs={gridConfig.item}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      return (
                        <Tooltip
                          title={field.value || ""}
                          arrow
                          placement="top"
                        >
                          <Box
                            bgcolor={itemBackgroundColor}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <MiInputTextbox
                              {...field}
                              size="small"
                              variant="outlined"
                              label="Item"
                              error={errors.name}
                              required
                              textfieldProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                    color: "black",
                                  }, // check this later
                                  textDecoration: isItemDeleted
                                    ? "line-through"
                                    : "none",
                                },
                                inputProps: { style: { textAlign: "center" } },
                              }}
                              helperText={errors.name?.message}
                              fullWidth
                              readOnly={isFormReadOnly}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>
                <GridLegacy item xs={gridConfig.quantity}>
                  <Controller
                    name="quantity"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      return (
                        <Tooltip
                          title={field.value || ""}
                          arrow
                          placement="top"
                        >
                          <Box
                            bgcolor={itemBackgroundColor}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <MiInputTextbox
                              {...field}
                              size="small"
                              label="Qty"
                              type="number"
                              variant="outlined"
                              error={errors.quantity}
                              required
                              textfieldProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                },
                                inputProps: { style: { textAlign: "center" } },
                              }}
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "black",
                                  color: "black",
                                },
                              }}
                              helperText={errors.name?.message}
                              fullWidth
                              readOnly={isFormReadOnly}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>
                <GridLegacy item xs={gridConfig.unitPrice}>
                  <Controller
                    name="unitPrice"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      return (
                        <Tooltip
                          title={
                            field.value !== "" && field.value != null
                              ? `$${parseFloat(field.value).toFixed(2)}`
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            bgcolor={itemBackgroundColor}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <NumericFormat
                              value={field.value ?? ""}
                              customInput={MiInputTextbox}
                              thousandSeparator
                              required
                              label="Price"
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative
                              allowLeadingZeros={false}
                              prefix="$"
                              textfieldProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                    color: "black",
                                  },
                                  textDecoration: isItemDeleted
                                    ? "line-through"
                                    : "none",
                                },
                                inputProps: { style: { textAlign: "center" } },
                              }}
                              name={field.name}
                              onValueChange={(values) => {
                                field.onChange(values.value);
                              }}
                              error={errors?.unitPrice}
                              helperText={errors?.unitPrice?.message}
                              readOnly={isFormReadOnly}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>

                {itemType === ITEM_TYPES.INVOICE && (
                  <GridLegacy item xs={gridConfig.total}>
                    <Tooltip
                      title={getCurrencyString(totalValueOfItem)}
                      arrow
                      placement="top"
                    >
                      <Box
                        bgcolor="grey.200"
                        borderRadius={2}
                        border="1px solid"
                        borderColor="divider"
                        width="100%"
                      >
                        <MiInputTextbox
                          readOnly
                          label="Total"
                          value={getCurrencyString(totalValueOfItem)}
                          placeholder="$0.00"
                          fullWidth
                          textfieldProps={{
                            sx: {
                              fontSize: "0.875rem",
                            },
                            inputProps: { style: { textAlign: "center" } },
                          }}
                          sx={{
                            "& .MuiInputLabel-root.Mui-disabled": {
                              color: "black",
                            },
                            // Input text color when disabled
                            "& .MuiInputBase-input.Mui-disabled": {
                              WebkitTextFillColor: "black", // needed for Chrome
                              color: "black", // fallback
                            },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  </GridLegacy>
                )}
              </>
            )}

            {/* Additional fields based on item type */}
            {itemType === ITEM_TYPES.PAYMENT && (
              <>
                <GridLegacy item xs={gridConfig.payment || 6}>
                  <Controller
                    name="billingCommitmentId"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      const poLabel =
                        purchaseOrderOptions?.find(
                          (po) => po.value === field.value
                        )?.label || "";
                      field.value = isItemDeleted ? "" : field.value || "";

                      return (
                        <Tooltip title={poLabel} arrow placement="top">
                          <Box
                            bgcolor={theme.palette.common.white}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <MiInputSelectBox
                              {...field}
                              size="small"
                              disableNone
                              label="Payment Information"
                              variant="outlined"
                              value={field.value}
                              required
                              handleChange={(value) => {
                                field.onChange(value);
                              }}
                              sx={{
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "black",
                                  color: "black",
                                },
                                textDecoration: isItemDeleted
                                  ? "line-through"
                                  : "none",
                              }}
                              InputProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                },
                              }}
                              error={errors.billingCommitmentId}
                              helperText={errors.billingCommitmentId?.message}
                              readOnly={isFormReadOnly}
                              fullWidth
                              options={purchaseOrderOptions}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>

                <GridLegacy item xs={gridConfig.billingCommitmentAmount || 6}>
                  <Controller
                    name="paidOnBillingCommitment"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      return (
                        <Tooltip
                          title={
                            field.value !== "" && field.value != null
                              ? `$${parseFloat(field.value).toFixed(2)}`
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            bgcolor={theme.palette.common.white}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <NumericFormat
                              value={field.value ?? ""}
                              customInput={MiInputTextbox}
                              thousandSeparator
                              required
                              label="Paid"
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative
                              allowLeadingZeros={false}
                              prefix="$"
                              name={field.name}
                              onValueChange={(values) => {
                                field.onChange(values.value);
                              }}
                              textfieldProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                    color: "black",
                                  },
                                  textDecoration: isItemDeleted
                                    ? "line-through"
                                    : "none",
                                },
                                inputProps: { style: { textAlign: "center" } },
                              }}
                              error={errors.paidOnBillingCommitment}
                              helperText={
                                errors.paidOnBillingCommitment?.message
                              }
                              readOnly={isFormReadOnly}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>
              </>
            )}

            {itemType === ITEM_TYPES.VARIANCE && (
              <>
                <GridLegacy item xs={gridConfig.name || 4}>
                  <MiStackedTypography
                    title={
                      item.name
                        ? `${item.name} Invoice Variance`
                        : "Invoice Variance"
                    }
                    description={`${formatDate(
                      item.originatingInvoice.invoicedDate,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                    )}`}
                    titleProps={{
                      fontWeight: "600",
                      color: "text.primary",
                      variant: "body2",
                    }}
                    descriptionProps={{
                      color: "text.secondary",

                      variant: "caption",
                    }}
                  />
                </GridLegacy>
                <GridLegacy item xs={gridConfig.openBalance || 4}>
                  <Tooltip title={item.openBalance} arrow placement="top">
                    <Box
                      bgcolor="grey.200"
                      borderRadius={2}
                      border="1px solid"
                      borderColor="divider"
                      width="100%"
                    >
                      <MiInputTextbox
                        readOnly
                        value={formatOpenBalanceDisplay(item.openBalance)}
                        label="Price"
                        placeholder="0.00"
                        fullWidth
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "black",
                            color: "black",
                          },
                        }}
                        textfieldProps={{
                          sx: {
                            fontSize: "0.875rem",
                            // eslint-disable-next-line no-nested-ternary
                            color: isItemDeleted
                              ? "error.main"
                              : item.totalAmount < 0
                              ? "error.main"
                              : "inherit",

                            textDecoration: isItemDeleted
                              ? "line-through"
                              : "none",
                          },
                          inputProps: { style: { textAlign: "center" } },
                        }}
                      />
                    </Box>
                  </Tooltip>
                </GridLegacy>
                <GridLegacy item xs={gridConfig.pricePaid || 4}>
                  <Controller
                    name="unitPrice"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      return (
                        <Tooltip
                          title={
                            field.value !== "" && field.value != null
                              ? `$${parseFloat(field.value).toFixed(2)}`
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            bgcolor={theme.palette.common.white}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <NumericFormat
                              value={field.value ?? ""}
                              customInput={MiInputTextbox}
                              thousandSeparator
                              required
                              label="Paid"
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              allowLeadingZeros={false}
                              prefix="$"
                              name={field.name}
                              onValueChange={(values) => {
                                field.onChange(values.value);
                              }}
                              textfieldProps={{
                                sx: {
                                  fontSize: "0.875rem",
                                  color: getColorOfInvoiceItem(),
                                  "& .MuiInputBase-input.Mui-disabled": {
                                    WebkitTextFillColor: "black",
                                    color: "black",
                                  },
                                  textDecoration: isItemDeleted
                                    ? "line-through"
                                    : "none",
                                },
                                inputProps: { style: { textAlign: "center" } },
                              }}
                              error={errors.unitPrice}
                              helperText={errors.unitPrice?.message}
                              readOnly={isFormReadOnly}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>
                <GridLegacy item xs={gridConfig.invoiceId || 4}>
                  <Controller
                    name="invoiceId"
                    control={control}
                    render={({ field: { ref, ...field } }) => {
                      const label =
                        purchaseOrderOptions?.find(
                          (po) => po.value === field.value
                        )?.label || "";
                      field.value = isItemDeleted ? "" : field.value || "";
                      return (
                        <Tooltip title={label} arrow placement="top">
                          <Box
                            bgcolor={theme.palette.common.white}
                            borderRadius={2}
                            border="1px solid"
                            borderColor="divider"
                            width="100%"
                          >
                            <MiInputSelectBox
                              {...field}
                              size="small"
                              disableNone
                              label="Invoice"
                              variant="outlined"
                              value={field.value}
                              sx={{
                                "& .MuiOutlinedInput-input.MuiSelect-select": {
                                  padding: "8px 12px",
                                  paddingRight: "14px",
                                },
                                "& .MuiInputBase-input.Mui-disabled": {
                                  WebkitTextFillColor: "black",
                                  color: "black",
                                },
                              }}
                              required
                              handleChange={(value) => {
                                field.onChange(value);
                              }}
                              InputProps={{
                                sx: {
                                  fontSize: "0.875rem",

                                  "& .MuiInputBase-input": {
                                    textAlign: isFormReadOnly
                                      ? "center"
                                      : "left",
                                  },
                                },
                              }}
                              error={errors.invoiceId}
                              helperText={errors.invoiceId?.message}
                              readOnly={isFormReadOnly}
                              fullWidth
                              options={invoiceIdOptions}
                            />
                          </Box>
                        </Tooltip>
                      );
                    }}
                  />
                </GridLegacy>
              </>
            )}
          </GridLegacy>
        </GridLegacy>
        {activeStep !== RECONCILIATION_STEPS.REVIEW_SUBMIT &&
          viewMode !== INVOICING_ROUTES.INVOICED &&
          (invoiceStatus === InvoiceStatus.Draft ||
            itemType === ITEM_TYPES.VARIANCE) && (
            <GridLegacy
              item
              display="flex"
              ml={1}
              sx={{ alignSelf: "stretch", justifyContent: "center" }}
            >
              {(() => {
                if (isItemDeleted) return null;
                if (isSubmitting) return renderSubmittingState();
                if (isEditing) return renderEditingControls();
                return renderDefaultControls();
              })()}
            </GridLegacy>
          )}
      </GridLegacy>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  gridHeader: {
    padding: theme.spacing(0, 1),
  },
  gridCell: {
    padding: theme.spacing(2),
  },
  checkboxCell: {
    padding: theme.spacing(0),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
}));
